import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { MenuItem, SxProps, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import React from 'react';
import { DossierOpenSearch } from '@europrocurement/l2d-domain';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { useModal } from '@europrocurement/flexy-components';
import { ApiDossiersIdActionPutActionEnum } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { useHandleError } from '@europrocurement/l2d-hooks';
import { useNavigate } from 'react-router-dom';
import useTransition from './useTransition';
import { DossierAction } from '../providers/DossierActionContext';
import DossierFormValidationStatusModal from '../components/detail/DossierFormValidationStatusModal/DossierFormValidationStatusModal';
import useAdditionnalCollectionRequest from './useAdditionnalCollectionRequest';

type useDossierActionMenuItemsProps = {
    dossier: DossierOpenSearch;
    setAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
    triggerAction: (action: DossierAction, selectedDossier: Array<DossierOpenSearch>) => void;
    refreshDossier: () => void;
    getOrderDuplicateRoute?: (id: number) => string;
};
const useDossierActionMenuItems = ({
    dossier,
    triggerAction,
    setAnchorEl,
    refreshDossier,
    getOrderDuplicateRoute,
}: useDossierActionMenuItemsProps) => {
    const { enqueueSnackbar } = useSnackbar();
    const { canApplyTransition, applyTransition } = useTransition();
    const { modalActions } = useModal();
    const { apiErrorMessageSnackbar } = useHandleError();
    const navigate = useNavigate();
    const canSuspend = canApplyTransition(dossier, 'suspend');
    const canUnSuspend = canApplyTransition(dossier, 'unsuspend');
    const canCancel = canApplyTransition(dossier, 'cancel');
    const canAssign = true;
    const { callAdditionnalCollectionRequestModal } = useAdditionnalCollectionRequest();

    const handleOpenModalDossierAction = (action: DossierAction) => {
        triggerAction(action, [dossier]);
        setAnchorEl(null);
    };

    const handleUnExistingAlert = () => {
        enqueueSnackbar('Non implémenté', {
            variant: 'error',
        });
    };

    const handleSubmitFormStatus = async (
        actionIdentifier: ApiDossiersIdActionPutActionEnum | 'save',
    ) => {
        if (actionIdentifier === 'save') {
            modalActions.close();
        } else if (canApplyTransition(dossier, actionIdentifier)) {
            applyTransition(dossier, actionIdentifier)
                .then(() => {
                    modalActions.close();
                    refreshDossier();
                })
                .catch((err) => {
                    modalActions.close();
                    apiErrorMessageSnackbar(
                        err,
                        'Une erreur est survenue lors de la modification du dossier',
                    );
                });
        }
    };

    const handleUpdateDossierStatut = () => {
        // Display Modal to choose action
        modalActions.call(
            <DossierFormValidationStatusModal
                dossier={dossier}
                withoutSaveForm
                handleSubmit={handleSubmitFormStatus}
            />,
        );
    };

    type renderMenuItemProps = {
        key: string;
        action: () => void;
        iconName?: IconName;
        label?: string;
        textSx?: SxProps;
        iconSx?: SxProps;
    };
    const renderMenuItem = ({
        key,
        action,
        iconName = 'exclamation-circle',
        label = 'menu item',
        textSx,
        iconSx,
    }: renderMenuItemProps) => (
        <MenuItem
            key={key}
            onClick={action}
            sx={{ gap: '4px' }}
        >
            <FaOptionIcon
                icon={['fasl', iconName]}
                color="text.gris70"
                sx={iconSx}
            />
            <Typography sx={{ color: 'text.primary', ...textSx }}>{label}</Typography>
        </MenuItem>
    );

    const editDossierMenuItem = renderMenuItem({
        key: 'updateDossierActionItem',
        action: handleUnExistingAlert,
        iconName: 'file-pen',
        label: 'Modifier le dossier',
    });

    const groupDossierMenuItem = renderMenuItem({
        key: 'groupDossierActionItem',
        action: () => handleOpenModalDossierAction('manageGroups'),
        iconName: 'object-group',
        label: 'Déplacer dans un groupe',
    });

    const exportDossierMenuItem = renderMenuItem({
        key: 'exportActionItem',
        action: () => handleOpenModalDossierAction('export'),
        iconName: 'paper-plane',
        label: 'Envoyer un récapitulatif',
    });

    const returnAssignUserDossierMenuItem = () => {
        if (canAssign) {
            return renderMenuItem({
                key: 'assignUserActionItem',
                action: () => handleOpenModalDossierAction('assign'),
                iconName: 'user',
                label: 'Assigner à un formaliste',
            });
        }

        return undefined;
    };

    const urgentDossierMenuItem = renderMenuItem({
        key: 'urgentActionItem',
        action: () => handleOpenModalDossierAction('urgent'),
        iconName: 'exclamation-circle',
        label: 'Marquer comme urgent',
    });

    const archiveDossierMenuItem = renderMenuItem({
        key: 'archiveActionItem',
        action: handleUnExistingAlert,
        iconName: 'archive',
        label: 'Archiver le dossier',
    });

    const duplicateDossierMenuItem = renderMenuItem({
        key: 'duplicatedActionItem',
        action: () => {
            if (getOrderDuplicateRoute) {
                navigate(getOrderDuplicateRoute(dossier.collectionId as number));
            }
        },
        iconName: 'clone',
        label: 'Dupliquer la commande',
    });

    const returnSuspendDossierMenuItem = () => {
        if (canSuspend) {
            return renderMenuItem({
                key: 'suspendActionItem',
                action: () => handleOpenModalDossierAction('suspend'),
                iconName: 'pause',
                label: 'Suspendre le dossier',
            });
        }

        return undefined;
    };

    const returnUnSuspendDossierMenuItem = () => {
        if (canUnSuspend) {
            return renderMenuItem({
                key: 'unsuspendActionItem',
                action: () => handleOpenModalDossierAction('unsuspend'),
                iconName: 'backward-step',
                label: 'Reprendre le dossier',
            });
        }

        return undefined;
    };

    const returnCancelDossierMenuItem = () => {
        if (canCancel) {
            return renderMenuItem({
                key: 'cancelActionItem',
                action: () => handleOpenModalDossierAction('cancel'),
                iconName: 'ban',
                label: 'Annuler',
                textSx: { color: 'error.main' },
                iconSx: { color: 'error.main' },
            });
        }

        return undefined;
    };

    const updateDossierStatus = renderMenuItem({
        key: 'statusDossierItem',
        action: () => handleUpdateDossierStatut(),
        iconName: 'arrow-progress',
        label: 'Modifier le statut du dossier',
    });

    const additionnalClientRequest = renderMenuItem({
        key: 'additionnalRequestItem',
        action: () => callAdditionnalCollectionRequestModal(),
        iconName: 'paper-plane',
        label: 'Demande complémentaire',
    });

    const dossierActionMenuItemsFront: React.JSX.Element[] = [
        groupDossierMenuItem,
        returnSuspendDossierMenuItem(),
        returnUnSuspendDossierMenuItem(),
        returnCancelDossierMenuItem(),
    ].filter((item) => item !== undefined);

    const dossierActionMenuItemsBack: React.JSX.Element[] = [
        editDossierMenuItem,
        groupDossierMenuItem,
        exportDossierMenuItem,
        returnAssignUserDossierMenuItem(),
        urgentDossierMenuItem,
        archiveDossierMenuItem,
        returnSuspendDossierMenuItem(),
        returnUnSuspendDossierMenuItem(),
    ].filter((item) => item !== undefined);

    const dossierDetatilQuickActionBack: React.JSX.Element[] = [
        updateDossierStatus,
        additionnalClientRequest,
        exportDossierMenuItem,
        groupDossierMenuItem,
        returnAssignUserDossierMenuItem(),
        returnSuspendDossierMenuItem(),
        returnUnSuspendDossierMenuItem(),
        urgentDossierMenuItem,
        archiveDossierMenuItem,
        duplicateDossierMenuItem,
        // editDossierMenuItem,
    ].filter((item) => item !== undefined);

    // Both of the actions are conditionnal, it's better to contionnaly insert action in order to sent an empty array to the client
    const dossierDetatilQuickActionFront: React.JSX.Element[] = [
        returnSuspendDossierMenuItem(),
        returnCancelDossierMenuItem(),
    ].filter((item) => item !== undefined);

    const getDossierDetailActionBySource = (source: 'FRONT' | 'BACK'): React.JSX.Element[] => {
        if (source === 'BACK') {
            return dossierDetatilQuickActionBack;
        }
        return dossierDetatilQuickActionFront;
    };

    return {
        dossierActionMenuItemsFront,
        dossierActionMenuItemsBack,
        dossierDetatilQuickActionBack,
        dossierDetatilQuickActionFront,
        getDossierDetailActionBySource,
    };
};

export default useDossierActionMenuItems;
